import { draftToHtmlWithLineBreaks } from 'util/draftToHtmlWithLineBreaks';

import { createAsyncThunk } from '@reduxjs/toolkit';
import { ScheduleModel } from 'api/dto/Schedule.dto';
import { STORE_NAME } from 'reduxStore/emailDistribution/initialState';
import { serializeError } from 'shared/utils/redux';
import {
  formValueSelector,
  getFormSyncErrors,
  getFormValues,
  SubmissionError,
  submit,
  touch,
} from 'redux-form';
import { AdminFormNames } from 'register/AdminFormNames';
import { updateLanguageTab } from 'reduxStore/page/slice';
import {
  clearCurrentEmailTemplate,
  clearEmailTemplateByIdByLanguage,
} from 'reduxStore/emailDistribution/slice';
import {
  EmailTemplateTranslation,
  mapEmailTemplateFormValuesToTranslations,
} from 'api/dto/old/EmailTemplateTranslations.dto';
import { languageConfig } from 'translations/languageConfig';
import { ValidationErrorException } from 'exception/ValidationErrorException';
import { LangCodeEnum } from 'shared/enums/LangCodeEnum';
import { convertToRaw } from 'draft-js';
import { selectEmailDistribution } from 'reduxStore/emailDistribution/selectors';
import { openSnackbar } from 'reduxStore/snackbars/asyncActions';
import { getIsBaseLanguage } from 'shared/utils/getIsBaseLanguage';
import { scheduleApi } from 'container/scheduleApi';
import { emailTemplateApi } from 'container/emailTemplateApi';
import { emailDistributionApi } from 'container/emailDistributionApi';
import { EmailTemplateModel } from 'api/dto/EmailTemplate.dto';
import { selectHasClientMatch } from 'reduxStore/client/selectors';
import {
  selectCurrentProjectId,
  selectSubProjectCurrentId,
} from 'reduxStore/project/projectSelector';
import {
  selectChangedEmailTemplateLanguages,
  selectEmailTemplateLanguagesWithValidationErrors,
} from 'reduxStore/page/sharedSelectors';
import { PulseEmailModalForm } from 'view/SubProjectEmailPage/PulseEmailModal/PulseEmailModalForm';
import { closeModal } from 'reduxStore/modal/slice';

export const fetchEmailDistribution = createAsyncThunk(
  `${STORE_NAME}/fetchEmailDistribution`,
  async ({ projectId }: { projectId: number }) => {
    return emailDistributionApi.get(projectId);
  },
  { serializeError },
);

export const createEmailTemplate = createAsyncThunk(
  `${STORE_NAME}/createEmailTemplate`,
  ({ emailDistributionId, email }: { emailDistributionId: string; email: EmailTemplateModel }) =>
    emailTemplateApi.create(emailDistributionId, email),
  { serializeError },
);

export const updateEmailTemplate = createAsyncThunk(
  `${STORE_NAME}/updateEmailTemplate`,
  ({ email }: { email: EmailTemplateModel }) => emailTemplateApi.update(email),
  { serializeError },
);

export const saveEmailTemplateTranslations = createAsyncThunk(
  `${STORE_NAME}/saveEmailTemplateTranslations`,
  ({
    emailTemplateId,
    translations,
  }: {
    emailTemplateId: number;
    translations: EmailTemplateTranslation[];
  }) => {
    return emailTemplateApi.saveEmailTemplateTranslations({ emailTemplateId, translations });
  },
  { serializeError },
);

export const getTranslatedEmailTemplate = createAsyncThunk(
  `${STORE_NAME}/getTranslatedEmailTemplate`,
  ({ emailTemplateId, language }: { emailTemplateId: number; language: LangCodeEnum }) => {
    return emailTemplateApi.getTranslatedEmailTemplate({ emailTemplateId, language });
  },
  { serializeError },
);

export const scheduleEmailTemplate = createAsyncThunk(
  `${STORE_NAME}/scheduleEmailTemplate`,
  async (
    { emailTemplateId, schedule }: { emailTemplateId: number; schedule: ScheduleModel },
    { rejectWithValue },
  ) => {
    try {
      return await scheduleApi.schedule(emailTemplateId, schedule);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError },
);

export const scheduleEmailTemplateRelativeDate = createAsyncThunk(
  `${STORE_NAME}/scheduleEmailTemplateRelativeDate`,
  async (
    { emailTemplateId, schedule }: { emailTemplateId: number; schedule: ScheduleModel },
    { rejectWithValue },
  ) => {
    try {
      return await scheduleApi.scheduleRelativeDate(emailTemplateId, schedule);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError },
);

export const updateInvitationSendTime = createAsyncThunk(
  `${STORE_NAME}/updateInvitationSendTime`,
  async (
    { emailTemplateId, sendAtTime }: { emailTemplateId: number; sendAtTime: string | null },
    { rejectWithValue },
  ) => {
    try {
      return await scheduleApi.updateInvitationSendTime(emailTemplateId, sendAtTime);
    } catch (error) {
      return rejectWithValue(error);
    }
  },
  { serializeError },
);

export const unscheduleEmailTemplate = createAsyncThunk(
  `${STORE_NAME}/unscheduleEmailTemplate`,
  async ({ emailTemplateId, scheduleId }: { emailTemplateId: number; scheduleId: number }) => {
    return scheduleApi.unschedule(emailTemplateId, scheduleId);
  },
  { serializeError },
);

export const sendTestEmail = createAsyncThunk(
  `${STORE_NAME}/sendTestEmail`,
  async ({ language }: { language?: LangCodeEnum }, { dispatch, getState }) => {
    const isBaseLanguage = getIsBaseLanguage(language);

    const formName =
      language && !isBaseLanguage
        ? `${AdminFormNames.PulseEmailTemplateTranslation}:${language}`
        : AdminFormNames.PulseEmailTemplate;

    const selector = formValueSelector(formName);
    const state = getState();
    const title = selector(state, 'title');
    const sendTestTo = selector(state, 'sendTestTo');
    const content = selector(state, 'content');
    const fromName = selector(state, 'fromName');

    const htmlContent = content
      ? draftToHtmlWithLineBreaks(convertToRaw(content.getCurrentContent()))
      : '';
    const emailDistribution = selectEmailDistribution(state);

    return emailDistributionApi
      .test({
        distributionId: emailDistribution.id,
        to: sendTestTo.split(',').map((to: string) => to.trim()),
        content: htmlContent,
        subject: title,
        fromName,
      })
      .then(() => dispatch(openSnackbar({ message: 'Test email sent successfully' })))
      .catch(() => dispatch(openSnackbar({ message: 'Cannot send test email - error occurred' })));
  },
  { serializeError },
);

export const submitPulseEmailModalForm = createAsyncThunk<
  void,
  { email: EmailTemplateModel; shouldCloseModal?: boolean }
>(
  `${STORE_NAME}/submitPulseEmailModalForm`,
  async ({ email, shouldCloseModal = false }, { dispatch, getState }) => {
    const formName = AdminFormNames.PulseEmailTemplate;

    dispatch(submit(formName));

    // Should be after dispatch submit for validation to work properly
    const state = getState();
    const errors = getFormSyncErrors(formName)(state);
    if (Object.keys(errors).length > 0) {
      return;
    }

    const translationFormName = AdminFormNames.PulseEmailTemplateTranslation;
    const data = getFormValues(formName)(state) as PulseEmailModalForm.FormData;

    const emailTemplate = {
      id: email?.id || undefined,
      type: email?.type || 'reminder',
      content: data.content ? convertToRaw(data.content.getCurrentContent()) : null,
      title: data.title,
      senderName: data.fromName,
      replyTo: data.replyTo,
    };
    const languagesWithValidationErrors = selectEmailTemplateLanguagesWithValidationErrors(
      translationFormName,
      emailTemplate.id,
    )(state);
    const changedLanguages = selectChangedEmailTemplateLanguages(
      translationFormName,
      emailTemplate.id,
    )(state);

    const emailDistribution = selectEmailDistribution(state);
    const projectId = selectHasClientMatch(state)
      ? selectSubProjectCurrentId(state)
      : selectCurrentProjectId(state);

    try {
      if (languagesWithValidationErrors.length > 0) {
        const [firstLanguageWithValidationErrors] = languagesWithValidationErrors;
        dispatch(
          touch(
            `${AdminFormNames.PulseEmailTemplateTranslation}:${firstLanguageWithValidationErrors}`,
            'title',
          ),
        );
        dispatch(updateLanguageTab(firstLanguageWithValidationErrors));
        return;
      }

      if (email) {
        await dispatch(updateEmailTemplate({ email: emailTemplate }));
      } else {
        await dispatch(
          createEmailTemplate({ emailDistributionId: emailDistribution.id, email: emailTemplate }),
        );
      }

      const translations = changedLanguages.map((language) => {
        const formName = `${AdminFormNames.PulseEmailTemplateTranslation}:${language}`;
        const formValues = getFormValues(formName)(state) as PulseEmailModalForm.FormData;

        const emailTemplate = {
          id: email?.id || undefined,
          type: email?.type || 'reminder',
          content: formValues.content ? convertToRaw(formValues.content.getCurrentContent()) : null,
          title: formValues.title,
        };

        return mapEmailTemplateFormValuesToTranslations({ language, emailTemplate });
      });

      if (translations.length > 0) {
        await dispatch(
          saveEmailTemplateTranslations({ emailTemplateId: emailTemplate.id, translations }),
        );
      }

      await dispatch(fetchEmailDistribution({ projectId }));

      dispatch(
        openSnackbar({
          message: `Email ${email ? 'updated' : 'added'} successfully`,
        }),
      );

      if (shouldCloseModal) {
        dispatch(clearEmailTemplateByIdByLanguage());
        dispatch(clearCurrentEmailTemplate());
        dispatch(updateLanguageTab(languageConfig.baseLanguage));
        dispatch(closeModal());
      }
    } catch (error) {
      if (error instanceof ValidationErrorException) {
        throw new SubmissionError({
          [error.getPath()]: error.getMessage(),
        });
      }

      dispatch(
        openSnackbar({
          message: `Cannot ${email ? 'update' : 'add'} email - ${error.getMessage()}`,
        }),
      );
    }
  },
);
